import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import { describeAuthError } from './dotglobal-auth-errors.js';

@customElement('dotglobal-sign-in')
export class DotglobalSignIn extends DeesElement {
  @property({ type: String })
  public accessor errorCode = '';

  @property({ type: Boolean })
  public accessor available = true;

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .logo { color: var(--accent); }
      h1 { font-family: var(--font-display); }
      .provider-mark {
        display: grid;
        place-items: center;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: color-mix(in srgb, var(--accent) 12%, transparent);
        color: var(--accent);
      }
      .notice {
        padding: 11px 12px;
        border: 1px solid color-mix(in srgb, var(--error) 32%, var(--border-default));
        border-radius: 8px;
        background: color-mix(in srgb, var(--error) 6%, transparent);
        color: var(--text-secondary);
        font-size: 12.5px;
        line-height: 1.5;
      }
    `,
  ];

  public render(): TemplateResult {
    const errorMessage = this.errorCode ? describeAuthError(this.errorCode) : '';
    return html`
      <div class="page content auth">
        <div class="authgrid">
          <div class="authpitch">
            ${this.logo(34)}
            <h1 style="font-size: 30px; line-height: 1.1; margin: 18px 0 10px; font-weight: 700; letter-spacing: -0.02em">Sign in to manage your work.</h1>
            <p class="sec" style="font-size: 14.5px; line-height: 1.6; margin: 0">
              Public code remains available without an account. Sign-in enables actions authorized through your current native foss.global memberships.
            </p>
            <div class="stack gap14" style="margin-top: 26px">
              ${this.benefit('Package', 'Manage packages', 'View private packages and manage package visibility or retained items.')}
              ${this.benefit('ShieldCheck', 'Server-side authorization', 'Browser actions use a short-lived opaque session instead of a pasted access token.')}
              ${this.benefit('KeyRound', 'Explicit account binding', 'An administrator must bind your exact identity-provider account before first use.')}
            </div>
          </div>

          <div class="authcard card">
            <div class="stack gap16">
              <div>
                <div style="font-weight: 700; font-size: 17px; font-family: var(--font-display); letter-spacing: -0.01em">Continue with idp.global</div>
                <div class="muted" style="font-size: 12.5px; margin-top: 5px; line-height: 1.5">foss.global does not receive or store your identity-provider password.</div>
              </div>
              ${errorMessage ? html`<div class="notice">${errorMessage}</div>` : ''}
              ${!this.available
                ? html`<div class="notice">Browser sign-in is currently unavailable.</div>`
                : ''}
              <button class="btn provider" ?disabled=${!this.available} @click=${() => this.startSignIn()}>
                <span class="provider-mark">${this.icon('Fingerprint')}</span>
                <span class="grow" style="text-align: left">
                  <b style="display: block; font-size: 13.5px">Sign in securely</b>
                  <span class="muted" style="font-size: 11.5px">Authorization code flow with PKCE</span>
                </span>
                ${this.icon('ArrowRight')}
              </button>
              <div class="muted" style="font-size: 11.5px; line-height: 1.55">
                Unknown identities are denied. Contact a foss.global administrator if your idp.global account has not been bound to an existing native user.
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private benefit(iconArg: string, titleArg: string, descriptionArg: string): TemplateResult {
    return html`
      <div class="row gap12" style="align-items: flex-start">
        <div class="agent-glyph" style="width: 34px; height: 34px">${this.icon(iconArg)}</div>
        <div>
          <div style="font-weight: 600; font-size: 13.5px">${titleArg}</div>
          <div class="muted" style="font-size: 12.5px; margin-top: 1px">${descriptionArg}</div>
        </div>
      </div>
    `;
  }

  private startSignIn(): void {
    this.dispatchEvent(new CustomEvent('dotglobal-signin', {
      bubbles: true,
      composed: true,
    }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${16}></dees-icon>`;
  }

  private logo(sizeArg = 26): TemplateResult {
    return html`
      <svg class="logo" width=${sizeArg} height=${sizeArg} viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M16 2.5 28 9.2v13.6L16 29.5 4 22.8V9.2z" fill="currentColor" opacity="0.12"></path>
        <path d="M16 2.5 28 9.2v13.6L16 29.5 4 22.8V9.2z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
        <circle cx="16" cy="16" r="6" stroke="currentColor" stroke-width="1.6"></circle>
        <path d="M10 16h12M16 10c2.4 2 2.4 10 0 12M16 10c-2.4 2-2.4 10 0 12" stroke="currentColor" stroke-width="1.3"></path>
      </svg>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-sign-in': DotglobalSignIn;
  }
}
